// src/pages/Lich.js
import React, { useState, useEffect, useCallback } from "react";
import { fetchData, addData, updateData, deleteData } from "../services/api";
import { chuNgayAmDayDu } from "../utils/amlich";
import { veDangNgay, veDangGio } from "../utils/ngay";
import {
  useThongBao,
  ThongBao,
  DangTai,
  docQuyen,
  hoiXoa,
  KhoiGon,
} from "../vo";

// ============================================================
// 📅 LỊCH — MỘT LỊCH DUY NHẤT (Đợt N2, 08/09/2026)
// ------------------------------------------------------------
// Trang này thay cho HAI component cũ:
//     EventList       "Lịch của tôi"   — mục riêng
//     FamilyCalendar  "Lịch chung"     — mục chung
//
// Chúng ghi vào CÙNG bảng `events`, và `GET /api/:table` vốn đã trả về cả
// mục riêng lẫn mục chung — nên sự kiện chung hiện ra ở CẢ HAI ô, trên hai
// trang khác nhau. Một kho, hai cửa, hai màn hình.
//
// Chân dung 4.1 nói rõ ngay từ đầu:
//   *"Lịch là của riêng từng người. Ai muốn cả nhà biết thì tick một ô."*
//
// Cột `is_shared` đã có sẵn từ lâu. Nó chỉ bị HIỂU thành "hai loại lịch"
// thay vì "một ô tick". Đợt này sửa đúng chỗ hiểu sai đó.
//
// ⚠️ AI SỬA ĐƯỢC GÌ — luật ĐÃ ĐỔI chiều 08/09/2026:
//   • Mục của CHÍNH MÌNH   → toàn quyền, dù chung hay riêng
//   • Đăng mục cho cả nhà → AI CŨNG ĐƯỢC, không phải xin ai
//   • Mục chung của người khác → cần `lich.soan` (để Ba Mẹ dọn giúp)
//
// Bản đầu bắt phải có `lich.soan` mới đăng được cho cả nhà. Chủ nhà xem
// chạy thật rồi bác: *"đây là 'đuổi' tụi nó khỏi xài app, có cái đó mà cũng
// nhờ."* Đừng siết lại.
//
// Máy chủ mới là nơi giữ luật đó (`/api/lich` trong server.js). Giao diện
// chỉ ẩn/hiện nút cho đỡ chưng hửng — KHÔNG được coi là hàng rào. Ai gõ
// thẳng vào API vẫn bị máy chủ chặn.
// ============================================================

const homNay = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const veNgayDep = (chuoi) =>
  new Date(chuoi).toLocaleDateString("vi-VN", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

/** Một dòng trong lịch. Phải nằm NGOÀI component cha — xem bài học 06. */
function DongLich({ sk, suaDuoc, onSua, onXoa }) {
  return (
    <li className="list-item lich-dong">
      <div className="lich-noi-dung">
        <div className="lich-ten">
          {sk.title}
          {sk.is_shared && (
            <span className="lich-nhan-chung" title="Cả nhà đều thấy mục này">
              📢 Cả nhà biết
            </span>
          )}
        </div>
        <div className="lich-phu">
          {veNgayDep(sk.date)}
          <span className="ngay-am">{chuNgayAmDayDu(sk.date)}</span>
          {sk.time && ` · ${sk.time}`}
          {sk.is_shared && sk.ten_nguoi_tao && ` · ${sk.ten_nguoi_tao} thêm`}
        </div>
        {sk.note && <div className="lich-ghi-chu">{sk.note}</div>}
        {sk.reminder && (
          <div className="lich-nhac">
            ⏰ Nhắc ngày {new Date(sk.reminder).toLocaleDateString("vi-VN")}
          </div>
        )}
      </div>

      {suaDuoc && (
        <div className="viec-nut">
          <button onClick={onSua} className="btn-edit" title="Sửa">
            ✏️
          </button>
          <button onClick={onXoa} className="btn-delete" title="Xóa">
            🗑️
          </button>
        </div>
      )}
    </li>
  );
}

function Lich({ user }) {
  const [ds, setDs] = useState([]);
  const [dangTai, setDangTai] = useState(true);
  const [hienDaQua, setHienDaQua] = useState(false);
  const { bao, noiBao } = useThongBao();

  // Biểu mẫu
  const [moForm, setMoForm] = useState(false);
  const [suaId, setSuaId] = useState(null);
  const [ten, setTen] = useState("");
  const [ngay, setNgay] = useState("");
  const [gio, setGio] = useState("");
  const [ghiChu, setGhiChu] = useState("");
  const [nhac, setNhac] = useState("");
  const [choCaNhaBiet, setChoCaNhaBiet] = useState(false);

  const q = docQuyen(user);
  // Quyền DỌN mục chung của người khác — KHÔNG phải quyền đăng.
  const donGiupDuoc = q.rieng("lich", "soan");

  const taiLai = useCallback(async () => {
    try {
      setDs(await fetchData("lich"));
    } catch (err) {
      noiBao("⚠️ Không tải được lịch");
    } finally {
      setDangTai(false);
    }
  }, [noiBao]);

  useEffect(() => {
    taiLai();
  }, [taiLai]);

  const donForm = () => {
    setTen("");
    setNgay("");
    setGio("");
    setGhiChu("");
    setNhac("");
    setChoCaNhaBiet(false);
    setSuaId(null);
    setMoForm(false);
  };

  const luu = async (e) => {
    e.preventDefault();
    if (!ten.trim()) return noiBao("⚠️ Chưa có tên sự kiện");
    if (!ngay) return noiBao("⚠️ Chưa chọn ngày");

    const duLieu = {
      title: ten.trim(),
      date: ngay,
      time: gio || null,
      note: ghiChu.trim() || null,
      reminder: nhac || null,
      is_shared: choCaNhaBiet,
    };

    try {
      if (suaId) await updateData("lich", suaId, duLieu);
      else await addData("lich", duLieu);
      donForm();
      taiLai();
      noiBao(suaId ? "✅ Đã cập nhật" : "✅ Đã thêm vào lịch");
    } catch (err) {
      noiBao("⚠️ Không lưu được");
    }
  };

  const moSua = (sk) => {
    setSuaId(sk.id);
    setTen(sk.title || "");
    setNgay(veDangNgay(sk.date));
    setGio(veDangGio(sk.time));
    setGhiChu(sk.note || "");
    setNhac(veDangNgay(sk.reminder));
    setChoCaNhaBiet(sk.is_shared === true);
    setMoForm(true);
  };

  const xoa = async (sk) => {
    if (!hoiXoa("sự kiện", sk.title)) return;
    try {
      await deleteData("lich", sk.id);
      taiLai();
      noiBao("🗑️ Đã xóa");
    } catch (err) {
      noiBao("⚠️ Không xóa được");
    }
  };

  if (dangTai) return <DangTai gi="lịch" />;

  // Ai được sửa mục nào — CÙNG luật với `duocSuaLich` trong
  // quan_gia_api/luat-quyen.js. Giao diện chỉ ẩn nút cho đỡ chưng hửng;
  // máy chủ mới là hàng rào thật.
  const suaDuoc = (sk) => {
    if (q.laAdmin) return true;
    if (sk.user_id === user.id) return true; // mục của chính mình
    return sk.is_shared && donGiupDuoc; // mục chung của người khác
  };

  const nay = homNay();
  const sapToi = [];
  const daQua = [];
  ds.forEach((sk) => {
    const d = new Date(veDangNgay(sk.date) + "T00:00:00");
    (d < nay ? daQua : sapToi).push(sk);
  });
  daQua.reverse(); // gần đây nhất lên trước

  const veDong = (sk) => (
    <DongLich
      key={sk.id}
      sk={sk}
      suaDuoc={suaDuoc(sk)}
      onSua={() => moSua(sk)}
      onXoa={() => xoa(sk)}
    />
  );

  return (
    <div className="card">
      <h3>📅 Lịch</h3>
      <ThongBao bao={bao} />

      <button
        onClick={() => (moForm ? donForm() : setMoForm(true))}
        className="btn-add"
      >
        {moForm ? "❌ Hủy" : "➕ Thêm vào lịch"}
      </button>

      {moForm && (
        <form onSubmit={luu} className="viec-form">
          <input
            type="text"
            placeholder="Sự kiện gì? (giỗ, sinh nhật, họp mặt…)"
            value={ten}
            onChange={(e) => setTen(e.target.value)}
            className="input-full"
            autoFocus
          />

          <div className="viec-hang-nhap">
            <label className="viec-o-nhap">
              <span>Ngày</span>
              <input
                type="date"
                value={ngay}
                onChange={(e) => setNgay(e.target.value)}
              />
            </label>
            <label className="viec-o-nhap">
              <span>Mấy giờ (không bắt buộc)</span>
              <input
                type="time"
                value={gio}
                onChange={(e) => setGio(e.target.value)}
              />
            </label>
            <label className="viec-o-nhap">
              <span>Nhắc vào ngày</span>
              <input
                type="date"
                value={nhac}
                onChange={(e) => setNhac(e.target.value)}
              />
            </label>
          </div>

          <input
            type="text"
            placeholder="Ghi chú thêm (không bắt buộc)"
            value={ghiChu}
            onChange={(e) => setGhiChu(e.target.value)}
            className="input-full"
          />

          {/* Ô TICK "CHO CẢ NHÀ BIẾT" — đúng chân dung 4.1.
              ⚠️ AI CŨNG THẤY Ô NÀY. Không ẩn theo quyền.
              Bản đầu chỉ hiện cho người có `lich.soan`; chủ nhà bác bỏ
              08/09/2026: *"có cái đó mà cũng nhờ"*. Đừng ẩn lại. */}
          <label className="lich-o-chung">
            <input
              type="checkbox"
              checked={choCaNhaBiet}
              onChange={(e) => setChoCaNhaBiet(e.target.checked)}
            />
            <span>
              <strong>📢 Cho cả nhà biết</strong>
              <em>
                Không tick thì đây là mục riêng của bạn, không ai khác thấy.
              </em>
            </span>
          </label>

          <button type="submit" className="btn-add">
            {suaId ? "✅ Cập nhật" : "✅ Thêm"}
          </button>
        </form>
      )}

      {ds.length === 0 && <p>Lịch còn trống.</p>}

      {sapToi.length > 0 && (
        <div className="viec-vung">
          <h4 className="viec-vung-ten">📌 Sắp tới</h4>
          <ul className="list">{sapToi.map(veDong)}</ul>
        </div>
      )}

      {sapToi.length === 0 && ds.length > 0 && (
        <p className="viec-vung-rong">🎉 Không có gì sắp tới.</p>
      )}

      <KhoiGon
        tieuDe="🕘 Đã qua"
        ds={daQua}
        veDong={veDong}
        mo={hienDaQua}
        onDoiMo={() => setHienDaQua(!hienDaQua)}
      />
    </div>
  );
}

export default Lich;
